import { VaultsController } from 'networking/controllers/vaults-controller';
import { useContext, useEffect, useState } from 'react';
import { useHandleApiError } from 'hooks/use-handle-api-error';
import { Button, ButtonStyle, ButtonType } from 'common/button';
import { Notification } from 'common/notification';
import { goToPage, RouteName } from 'routes';
import { getQueryParam } from 'helpers/utils';
import { appActions, AppContext } from 'context';
import { Vault } from 'models/vault';
import { TopicSelector } from './components/topic-selectors';
import styles from './onboarding-select-categories.module.scss';
import { LoadingScreen } from './components/loading-screen/loading-screen';

const SelectCategories: React.FC = () => {
  const vaultId = getQueryParam('vaultId') ?? '';
  const [showError, setShowError] = useState(false);
  const [suggestedQuestionsList, setSuggestedQuestionsList] = useState<
    PreBuiltQuestionsType[]
  >([]);
  const [selected, setSelected] = useState<PreBuiltQuestionsType[]>([]);

  const [loading, setLoading] = useState(false);

  const { dispatch } = useContext(AppContext);
  const [vault, setVault] = useState<VaultType>(new Vault());

  const setApiError = useHandleApiError(() => {
    setShowError(true);
  });

  const fetchVault = async () => {
    const response = await VaultsController.getVault(Number(vaultId));
    setVault(response);
  };

  useEffect(() => {
    fetchVault();
  }, []);

  useEffect(() => {
    if (Number(vaultId) !== -1) {
      dispatch({
        type: appActions.SET_CURRENT_VAULT,
        currentVault: vault,
      });
    }

    return () => {
      dispatch({
        type: appActions.SET_CURRENT_VAULT,
      });
    };
  }, [vault]);

  const fetchDefaultQuestions = async () => {
    try {
      const { suggestedQuestions } =
        await VaultsController.getDefaultQuestions();
      setSuggestedQuestionsList(
        suggestedQuestions.filter((item) => item.showOnboarding === true),
      );
    } catch (error: any) {
      setApiError(error);
    }
  };

  useEffect(() => {
    fetchDefaultQuestions();
  }, []);

  const handleNext = () => {
    setLoading(true);
    const timeoutId = setTimeout(() => {
      setLoading(false);
      goToPage(
        RouteName.OnboardingPromptSelectCategories,
        {},
        { vaultId, selected: JSON.stringify(selected) },
      );
    }, 2000);

    return () => clearTimeout(timeoutId);
  };

  const showErrorNotification = () => (
    <div className={styles.error}>
      <Notification handleClose={() => setShowError(false)} />
    </div>
  );

  const toggleSelection = (data: PreBuiltQuestionsType) => {
    setSelected((prev) => {
      let newSelected;
      if (prev.includes(data)) {
        newSelected = prev.filter((item) => item !== data);
      } else {
        newSelected = [...prev, data];
      }
      return newSelected.sort(
        (a, b) =>
          suggestedQuestionsList.findIndex((item) => item === a) -
          suggestedQuestionsList.findIndex((item) => item === b),
      );
    });
  };

  const showContent = () => (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <span className="text__heading3__gray800">
          What&apos;s important to your story?
        </span>
        <span className="text__body__regular__large__gray700">
          Select all that may apply. You can always add more categories later.
        </span>
      </div>
      <div>
        <TopicSelector
          suggestedQuestionsList={suggestedQuestionsList}
          selected={selected}
          toggleSelection={toggleSelection}
        />
        <div className={styles.containerButton}>
          <Button
            buttonType={ButtonType.Button}
            buttonStyle={ButtonStyle.PrimaryFilled}
            onClick={handleNext}
            disabled={selected.length === 0}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );

  const showLoadingContent = () => <LoadingScreen />;

  return (
    <div>
      {showError && showErrorNotification()}
      {!loading && showContent()}
      {loading && showLoadingContent()}
    </div>
  );
};

export { SelectCategories };
